import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateFollowupReasonRequestSchema,
  type CreateFollowupReasonRequest,
  type FollowupReasonDetail,
  type FollowupReasonListItem,
  UpdateFollowupReasonRequestSchema,
  type UpdateFollowupReasonRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateFollowupReason, useUpdateFollowupReason } from '../use-followup-reasons';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';

interface FollowupReasonFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  followupReason?: FollowupReasonDetail | FollowupReasonListItem | null;
}

const CREATE_DEFAULTS: CreateFollowupReasonRequest = {
  code: '',
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
};

/**
 * Field set shared by create + edit. Reads the form via context
 * (UzFormField → useFormContext), so it renders inside either UzForm.
 */
const FollowupReasonFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Customer requested callback"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive reasons are hidden from the follow-up form picker. Past follow-ups that referenced this reason stay intact."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const FollowupReasonFormDrawer = ({
  open,
  onOpenChange,
  mode,
  followupReason,
}: FollowupReasonFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createFollowupReason = useCreateFollowupReason();
  const updateFollowupReason = useUpdateFollowupReason();
  const pending = createFollowupReason.isPending || updateFollowupReason.isPending;

  const createForm = useForm<CreateFollowupReasonRequest>({
    resolver: zodResolver(CreateFollowupReasonRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateFollowupReasonRequest>({
    resolver: zodResolver(UpdateFollowupReasonRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (followupReason) {
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        name: followupReason.name,
        description: followupReason.description ?? '',
        sortOrder: followupReason.sortOrder,
        isActive: followupReason.isActive,
      });
    }
  }, [open, isCreate, followupReason, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field).
    await createFollowupReason.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!followupReason) return;
    await updateFollowupReason.mutateAsync({
      id: followupReason.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <FollowupReasonFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={
        isCreate ? 'Add follow-up reason' : `Edit ${followupReason?.name ?? 'follow-up reason'}`
      }
      description="The name is what agents see. The machine code is generated automatically."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
